import type { WidgetConfig } from './config';
import { trackCustomer } from './api';
import { resolvePageContext } from './trackingPipeline';

function parsePositiveInt(value: string | undefined): number {
  if (!value) return 0;
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function resolvePartnerTrackArgs(pathname: string, payPage = false): {
  bannerid: number;
  productid: number;
  categoryid: number;
  pay_page: 'Y' | 'N';
} {
  const ctx = resolvePageContext(pathname);
  let productid = parsePositiveInt(ctx.productId);
  let categoryid = parsePositiveInt(ctx.categoryId);

  const productMatch = pathname.match(/^\/product\/(\d+)/i);
  if (productMatch) productid = parsePositiveInt(productMatch[1]);
  const catNumMatch = pathname.match(/^\/cat\/(\d+)/i);
  if (catNumMatch) categoryid = parsePositiveInt(catNumMatch[1]);

  const isCheckout =
    payPage ||
    /^\/checkout(?:\/|$)/i.test(pathname) ||
    /^\/cart\/checkout/i.test(pathname);

  return {
    bannerid: 0,
    productid,
    categoryid,
    pay_page: isCheckout ? 'Y' : 'N',
  };
}

let lastTouchKey = '';
let lastTouchAt = 0;
const TOUCH_COOLDOWN_MS = 15000;

export async function touchPartnerClick(
  config: WidgetConfig,
  overrides: Partial<{ bannerid: number; productid: number; categoryid: number; pay_page: 'Y' | 'N' }> = {},
): Promise<void> {
  if (!config.puid && !config.partner) return;
  const base = resolvePartnerTrackArgs(window.location.pathname, overrides.pay_page === 'Y');
  const args = { ...base, ...overrides };
  const key = `${args.bannerid}:${args.productid}:${args.categoryid}:${args.pay_page}`;
  const now = Date.now();
  if (key === lastTouchKey && now - lastTouchAt < TOUCH_COOLDOWN_MS && args.pay_page !== 'Y') {
    return;
  }
  lastTouchKey = key;
  lastTouchAt = now;
  try {
    await trackCustomer(config, args.bannerid, args.productid, args.categoryid, args.pay_page);
  } catch {
    // non-blocking
  }
}

export function initPartnerClickTracking(config: WidgetConfig): void {
  if (typeof window === 'undefined' || window.location.pathname.includes('/preview-proxy')) return;

  const runInitial = () => { void touchPartnerClick(config); };
  runInitial();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInitial, { once: true });
  }
  ['shoplight:ready', 'shop-widget:ready', 'shop-widget-ready'].forEach((ev) => {
    window.addEventListener(ev, runInitial, { once: true });
  });

  window.addEventListener('sw:add-to-cart', (e: Event) => {
    const detail = (e as CustomEvent<{ productId?: string; product_id?: string }>).detail ?? {};
    const pid = parsePositiveInt(String(detail.product_id ?? detail.productId ?? ''));
    if (pid > 0) void touchPartnerClick(config, { productid: pid });
  });

  window.addEventListener('sw:checkout-start', () => {
    void touchPartnerClick(config, { pay_page: 'Y' });
  });
}
